import { type PropsWithChildren, useCallback } from 'react';
import type { UserThemePreference } from '@frameio/shared/contexts';
import { useAppUiTheme, useDispatchAppState } from '@frameio/shared/contexts';
import { useIsMobileNavOpen } from '@frameio/shared/contexts/AppStateContext';
import { setLocalDensityPreference } from '@frameio/shared/contexts/AppUiThemeContext/storeLocalDensity';
import { setLocalSizingPreference } from '@frameio/shared/contexts/AppUiThemeContext/storeLocalSizing';
import { getDevFlag } from '@frameio/shared/dev-flags';
import type {
  MenuButtonProps,
  ThemeDensity,
  ThemeSizing,
} from '@frameio/vapor';
import { MenuButton, MenuGroup, MenuRadio } from '@frameio/vapor';
import {
  IconComputerFill,
  IconFiletypeSpreadsheet,
  IconThemeDark,
  IconThemeLight,
  IconThemeSelect,
  IconWorkspace,
} from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/AppThemeSelectionMenuItem';

export type TextObject = {
  theme: {
    options: Partial<Record<UserThemePreference, string>>;
    preference: string;
  };
  density: {
    options: Record<ThemeDensity, string>;
    preference: string;
  };
  sizing: {
    options: Record<ThemeSizing, string>;
    preference: string;
  };
};

TEXT.DEFINE({
  theme: {
    options: {
      /** Theme option label for dark mode */
      dark: `Dark`,
      /** Theme option label for light mode */
      light: `Light`,
      /** Theme option label for following the system theme */
      auto: `System`,
    },
    /** Submenu group label for selecting a theme preference */
    preference: `Theme`,
  },
  density: {
    options: {
      /** Density option label for a compact UI layout */
      compact: `Compact`,
      /** Density option label for the default UI layout */
      regular: `Regular`,
      /** Density option label for a more spacious UI layout */
      spacious: `Spacious`,
    },
    /** Submenu group label for selecting UI density */
    preference: `Density`,
  },
  sizing: {
    options: {
      /** Sizing option label for small UI sizing */
      small: `Small`,
      /** Sizing option label for the default UI sizing */
      regular: `Regular`,
      /** Sizing option label for large UI sizing */
      large: `Large`,
    },
    /** Submenu group label for selecting UI sizing */
    preference: `Sizing`,
  },
});

const ICONS = {
  theme: {
    dark: <IconThemeDark />,
    light: <IconThemeLight />,
    auto: <IconComputerFill />,
  },
  density: {
    compact: <IconFiletypeSpreadsheet />,
    regular: <IconWorkspace />,
    spacious: <IconWorkspace />,
  },
  sizing: {
    small: <IconFiletypeSpreadsheet />,
    regular: <IconWorkspace />,
    large: <IconWorkspace />,
  },
};

export function AppThemeSelectionMenuItem() {
  const dispatch = useDispatchAppState();
  const isMobileNavOpen = useIsMobileNavOpen();

  const isDensitySizingThemingEnabled = getDevFlag(
    'ADAPTIVE_THEMING_DENSITY_SIZING'
  );

  const closeMobileNav = useCallback(() => {
    dispatch({ type: 'CLOSE_MOBILE_NAV' });
  }, [dispatch]);
  return (
    <MenuButton
      iconBefore={<IconThemeSelect />}
      submenu={
        <>
          <MenuGroup label={TEXT.theme.preference}>
            {Object.entries(TEXT.theme.options).map(([theme, textValue]) => (
              <AppThemeOption
                key={theme}
                iconBefore={ICONS.theme[theme as keyof typeof ICONS.theme]}
                onPress={closeMobileNav}
                shouldCloseMenuOnSelect={isMobileNavOpen}
                theme={theme as UserThemePreference}
              >
                {textValue}
              </AppThemeOption>
            ))}
          </MenuGroup>
          {isDensitySizingThemingEnabled && (
            <DensityAndSizingSubmenu
              closeMobileNav={closeMobileNav}
              isMobileNavOpen={isMobileNavOpen}
            />
          )}
        </>
      }
    >
      {TEXT.theme.preference}
    </MenuButton>
  );
}

/**
 * ** private routines **
 */

type AppThemeOptionProps = PropsWithChildren<MenuButtonProps> & {
  /**
   * @description the theme to set when the option is selected
   */
  theme: UserThemePreference;
};

/**
 * @private
 * @description renders a menu radio option for the app theme selection menu
 */
function AppThemeOption({
  children,
  theme,
  onPress,
  ...otherProps
}: AppThemeOptionProps) {
  const { appUiTheme, setAppUiTheme } = useAppUiTheme();
  // generate an internal interpolated name
  const name = `theme-preference--${theme}`.toLowerCase();
  // determine if the option is selected
  const isSelected = appUiTheme === theme;
  // help avoid network calls on selection of the same theme
  const handleSelect = () => {
    if (!isSelected) {
      setAppUiTheme(theme);
    }
    if (typeof onPress === 'function') {
      onPress();
    }
  };

  // the menu is open when the options are selected to preview the theme
  return (
    <MenuRadio
      {...otherProps}
      checked={isSelected}
      name={name}
      onPress={handleSelect}
    >
      {children}
    </MenuRadio>
  );
}

function DensityAndSizingSubmenu({
  isMobileNavOpen,
  closeMobileNav,
}: {
  isMobileNavOpen: boolean;
  closeMobileNav: () => void;
}) {
  const { appUiDensity, setAppUiDensity, appUiSizing, setAppUiSizing } =
    useAppUiTheme();
  return (
    <>
      <MenuGroup label={TEXT.density.preference}>
        {(
          Object.entries(TEXT.density.options) as Array<[ThemeDensity, string]>
        ).map(([densityType, textValue]) => (
          <MenuRadio
            key={densityType}
            checked={appUiDensity === densityType}
            iconBefore={ICONS.density[densityType]}
            name={`density-preference--${densityType}`}
            onPress={() => {
              if (appUiDensity !== densityType) {
                setLocalDensityPreference(densityType);
                setAppUiDensity(densityType);
              }
              closeMobileNav();
            }}
            shouldCloseMenuOnSelect={isMobileNavOpen}
          >
            {textValue}
          </MenuRadio>
        ))}
      </MenuGroup>
      <MenuGroup label={TEXT.sizing.preference}>
        {(
          Object.entries(TEXT.sizing.options) as Array<[ThemeSizing, string]>
        ).map(([sizingType, textValue]) => (
          <MenuRadio
            key={sizingType}
            checked={appUiSizing === sizingType}
            iconBefore={ICONS.sizing[sizingType]}
            name={`sizing-preference--${sizingType}`}
            onPress={() => {
              if (appUiSizing !== sizingType) {
                setLocalSizingPreference(sizingType);
                setAppUiSizing(sizingType);
              }
              closeMobileNav();
            }}
            shouldCloseMenuOnSelect={isMobileNavOpen}
          >
            {textValue}
          </MenuRadio>
        ))}
      </MenuGroup>
    </>
  );
}
